import type { PageId, Persona } from "./types";

interface NavItem {
  page: PageId;
  icon: string;
  label: string;
  badge?: number;
  title: string;
  /** Not wired up for this demo; shown but not navigable. */
  outOfScope?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface PersonaConfig {
  label: string;
  user: { name: string; initials: string; subtitle?: string };
  defaultPage: PageId;
  groups: NavGroup[];
}

export const PERSONA_NAV: Record<Persona, PersonaConfig> = {
  backoffice: {
    label: "Secretariaat / Bureau",
    user: { name: "Lies Vandenbergh", initials: "LV", subtitle: "Secretariaat EA SHW" },
    defaultPage: "overview",
    groups: [
      {
        label: "Overzicht",
        items: [
          {
            page: "overview",
            icon: "▦",
            label: "Dashboard",
            title: "EA SHW: Ethische Adviescommissie",
          },
          {
            page: "dossiers",
            icon: "≣",
            label: "Alle dossiers",
            title: "EA SHW: Alle dossiers",
            outOfScope: true,
          },
        ],
      },
      {
        label: "Workflow",
        items: [
          {
            page: "secretariaat",
            icon: "◫",
            label: "Reviewer toewijzen",
            badge: 7,
            title: "EA SHW: Reviewer toewijzen",
            outOfScope: true,
          },
          {
            page: "bureau",
            icon: "◉",
            label: "Bureau review",
            badge: 12,
            title: "EA SHW: Bureau review",
            outOfScope: true,
          },
          {
            page: "afgerond",
            icon: "✓",
            label: "Afgerond",
            title: "EA SHW: Afgeronde dossiers",
            outOfScope: true,
          },
        ],
      },
      {
        label: "Beheer",
        items: [
          {
            page: "audit",
            icon: "◎",
            label: "Audit trail",
            title: "EA SHW: Audit trail",
            outOfScope: true,
          },
          {
            page: "instellingen",
            icon: "⚙",
            label: "Instellingen",
            title: "EA SHW: Instellingen",
            outOfScope: true,
          },
          {
            page: "ai-regels",
            icon: "✦",
            label: "AI-regels",
            title: "EA SHW: AI-regels",
          },
        ],
      },
    ],
  },

  researcher: {
    label: "Onderzoeker",
    user: {
      name: "Els Vermeersch",
      initials: "EV",
      subtitle: "Faculteit Sociale Wetenschappen",
    },
    defaultPage: "mijn-aanvragen",
    groups: [
      {
        label: "Mijn aanvragen",
        items: [
          {
            page: "mijn-aanvragen",
            icon: "≣",
            label: "Mijn aanvragen",
            title: "Mijn ethische aanvragen",
          },
          {
            page: "nieuwe-aanvraag",
            icon: "+",
            label: "Nieuwe aanvraag",
            title: "Nieuwe aanvraag indienen",
          },
        ],
      },
      {
        label: "Hulp & gids",
        items: [
          {
            page: "vraagbaak",
            icon: "?",
            label: "Ethische Etna",
            title: "Ethische Etna: Tip Sheets-assistent",
          },
          {
            page: "hoe-werkt-het",
            icon: "ℹ",
            label: "Hoe werkt het?",
            title: "Hoe werkt het? Het review-proces",
          },
        ],
      },
    ],
  },

  promoter: {
    label: "Promotor",
    user: { name: "Prof. dr. Koen Willems", initials: "KW", subtitle: "Promotor · Faculteit OHW" },
    defaultPage: "team-aanvragen",
    groups: [
      {
        label: "Mijn onderzoekers",
        items: [
          {
            page: "team-aanvragen",
            icon: "◫",
            label: "Aanvragen team",
            title: "Aanvragen van mijn onderzoekers",
          },
        ],
      },
    ],
  },

  reviewer: {
    label: "Reviewer",
    user: { name: "Dr. Anke Peeters", initials: "AP", subtitle: "Reviewer EA SHW" },
    defaultPage: "bureau",
    groups: [
      {
        label: "Mijn reviews",
        items: [
          {
            page: "bureau",
            icon: "◉",
            label: "Mijn reviews",
            badge: 3,
            title: "EA SHW: Mijn reviews",
          },
        ],
      },
    ],
  },
};

const HIDDEN_PAGE_TITLES: Partial<Record<PageId, string>> = {
  "aanvraag-detail": "Aanvraag: detail & feedback",
};

export function getPageTitle(persona: Persona, pageId: PageId): string {
  for (const group of PERSONA_NAV[persona].groups) {
    for (const item of group.items) {
      if (item.page === pageId) {
        return item.title;
      }
    }
  }
  return HIDDEN_PAGE_TITLES[pageId] ?? "EA SHW";
}
